// Ported from field-rep-frontend's label explorer:
// src/internal/views/label-explorer/framePriority.ts

// Frame indexes here are 0-based.

const distance = (a: number, b: number) => Math.abs(a - b);

/**
 * The frame furthest from the target. On a tie, the one before it, since scrubbing mostly goes
 * forward.
 *
 * It decides which preview to drop when the cache is full: the one least likely to be needed soon,
 * so the frames around the slider stay ready and every step stays instant.
 */
export const farthestFrame = (
  target: number,
  frames: Iterable<number>,
): number | null => {
  let farthest: number | null = null;
  for (const frame of frames) {
    if (
      farthest === null ||
      distance(frame, target) > distance(farthest, target) ||
      (distance(frame, target) === distance(farthest, target) &&
        frame < farthest)
    ) {
      farthest = frame;
    }
  }
  return farthest;
};

/** The loaded frame nearest the target, the one after it on a tie. Null when none is loaded. */
export const nearestLoadedFrame = (
  target: number,
  loaded: Iterable<number>,
): number | null => {
  let nearest: number | null = null;
  for (const frame of loaded) {
    if (
      nearest === null ||
      distance(frame, target) < distance(nearest, target) ||
      (distance(frame, target) === distance(nearest, target) && frame > nearest)
    ) {
      nearest = frame;
    }
  }
  return nearest;
};

// Load order: the target and the 2 frames either side first, then the whole track in passes:
// every 8th frame, then every 4th, every 2nd and the rest.
const NEIGHBOURHOOD = 2;
const PASS_STRIDES = [8, 4, 2, 1];

/**
 * The frame to load next: the target's neighbourhood, then the whole track coarse to fine, nearest
 * to the target first within each pass. Skips frames loaded, loading or failed. Null when there's
 * none left, or when the cache is full and every frame left is no nearer than the furthest one it
 * holds (loading it would only evict a nearer one).
 *
 * Coarse to fine loading makes scrubbing usable before the whole track has downloaded: after a
 * second or two there's a loaded frame within a few of any position. Stopping when the cache is
 * full keeps a long track from downloading forever, dropping and reloading the same frames.
 */
export const nextFrameToLoad = ({
  frameCount,
  target: requestedTarget,
  loaded,
  loading,
  failed,
  capacity,
}: {
  frameCount: number;
  target: number;
  loaded: ReadonlySet<number>;
  loading: ReadonlySet<number>;
  /** Not retried, so a broken frame isn't requested over and over. */
  failed: ReadonlySet<number>;
  capacity: number;
}): number | null => {
  if (frameCount === 0) {
    return null;
  }

  // A target past either end (e.g. one left over from a longer track) starts from the nearest end,
  // so no frame is skipped.
  const target = Math.min(Math.max(requestedTarget, 0), frameCount - 1);
  const farthest =
    loaded.size >= capacity ? farthestFrame(target, loaded) : null;
  for (const frame of loadOrder(target, frameCount)) {
    if (loaded.has(frame) || loading.has(frame) || failed.has(frame)) {
      continue;
    }
    // A full cache skips frames as far as or farther than the ones it holds, but keeps looking: a
    // nearer one can still come later, in a finer pass.
    if (
      farthest !== null &&
      distance(frame, target) >= distance(farthest, target)
    ) {
      continue;
    }
    return frame;
  }
  return null;
};

/** The order frames load in, before skipping the loaded ones. */
function* loadOrder(target: number, frameCount: number) {
  for (const frame of framesOutwardFrom(target, frameCount)) {
    if (distance(frame, target) > NEIGHBOURHOOD) {
      break;
    }
    yield frame;
  }
  for (const stride of PASS_STRIDES) {
    for (const frame of framesOutwardFrom(target, frameCount)) {
      if (frame % stride === 0) {
        yield frame;
      }
    }
  }
}

/** Every frame, nearest the target first: e.g. from 4, the order is 4, 5, 3, 6, 2, 7, 1… */
function* framesOutwardFrom(target: number, frameCount: number) {
  yield target;
  for (let step = 1; step < frameCount; step++) {
    if (target + step < frameCount) {
      yield target + step;
    }
    if (target - step >= 0) {
      yield target - step;
    }
  }
}
